import { IonInput, IonItem, IonLabel, IonModal, IonSelect, IonSelectOption, IonSpinner, IonButton } from "@ionic/react";
import React, { useEffect, useRef, useState } from "react";
import countryCodes from "../country_code.json";
import { generateUniqueId } from "../hooks/common";
import { getUser, getUserByMobile, searchUser } from "../services/user.service";
import SelectItem from "./SelectItem";

// Accepts 8-12 digits -- covers both plain Indian mobiles (10) and the
// shorter/longer numbers Regex.mobile (common.service.ts, 7-15) already
// allows for non-Indian dealers, without auto-searching on every keystroke
// of a still-partial number the way an exact-length check would.
const MOBILE_SEARCH_PATTERN = /^\d{8,12}$/;
// Search fires this long after the user stops typing, not on every
// keystroke -- with a variable-length range there's no single "done typing"
// length to key off, so this is what stands in for it.
const MOBILE_SEARCH_DEBOUNCE_MS = 500;

// Purpose-built subset of CreateUser.tsx's dealer-creation fields for the
// embedded "create owner account" step of the Add Distributor/Retailer flow
// -- just the identity essentials (name, id, mobile, email, password,
// gender). Business name/GST/billing/shipping address now live on the
// InventoryLocation itself, not the owner account (see CreateUser.tsx).
// roles is fixed to dealer by the caller; there's no roles-picker here.
//
// There's a single "Dealer Mobile Number" field -- no separate
// search-existing-dealer step. Once the salesperson pauses typing on a
// plausible-length number, it's looked up automatically (a dealer often
// already has an account, e.g. they're getting a second shop, and
// re-registering them would either collide or create a confusing duplicate
// account). Everything below the mobile field -- the rest of
// DealerOwnerFields, plus the business/shop fields and media uploads in the
// parent -- stays disabled until that lookup resolves (found or genuinely
// not found), via onResolvedChange, so nothing gets typed that a "found"
// result would just overwrite. A lookup that errors (network blip, common
// enough in the field) does NOT count as "not found" -- it leaves the form
// locked and offers a retry instead, since silently treating an error as
// "no existing dealer" would defeat the whole point of this feature.
interface Props {
  formik: any;
  onResolvedChange?: (resolved: boolean) => void;
}

const DealerOwnerFields: React.FC<Props> = ({ formik, onResolvedChange }) => {
  const [showSelectCountryCodeModal, setShowSelectCountryCodeModal] = useState(false);
  const [searching, setSearching] = useState(false);
  // True once a lookup has genuinely resolved not-found for the mobile
  // number currently in the field. Distinct from "existingOwnerSelected"
  // (a found match) and from searchError (couldn't tell either way) -- only
  // a real not-found unlocks the "create a new dealer" fields.
  const [searchAttempted, setSearchAttempted] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const lastSearchedMobileRef = useRef("");
  // Bumped on every new lookup; a lookup only writes state if its token is
  // still current when it resolves. Without this, a slow-to-resolve search
  // for a number the user has since changed away from can land after a
  // faster, newer search already unlocked the form for a different number
  // -- silently overwriting what's on screen with the stale result.
  const searchTokenRef = useRef(0);

  const resolved = formik.values.existingOwnerSelected || (searchAttempted && !searching);

  useEffect(() => {
    onResolvedChange?.(resolved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolved]);

  function runSearch(mobile: string) {
    lastSearchedMobileRef.current = mobile;
    const token = ++searchTokenRef.current;
    setSearching(true);
    setSearchAttempted(false);
    setSearchError(false);
    getUserByMobile(mobile)
      .then((results) => {
        if (token !== searchTokenRef.current) return;
        const match = Array.isArray(results) ? results[0] : undefined;
        if (match) return selectExistingDealer(match, token);
        setSearchAttempted(true);
      })
      .catch(() => {
        if (token !== searchTokenRef.current) return;
        setSearchError(true);
      })
      .finally(() => {
        if (token !== searchTokenRef.current) return;
        setSearching(false);
      });
  }

  useEffect(() => {
    const mobile = formik.values.ownerMobile;
    if (formik.values.existingOwnerSelected) return;
    if (!MOBILE_SEARCH_PATTERN.test(mobile)) {
      if (searchAttempted) setSearchAttempted(false);
      if (searchError) setSearchError(false);
      lastSearchedMobileRef.current = "";
      return;
    }
    // Already tried this exact number (success, not-found, or error) --
    // don't auto-retry on every render. An errored search stays retryable
    // only via the explicit Retry tap below, not by silently re-firing.
    if (mobile === lastSearchedMobileRef.current) return;
    const timer = setTimeout(() => runSearch(mobile), MOBILE_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.ownerMobile, formik.values.existingOwnerSelected]);

  function setCountryCode(item: any) {
    formik.setFieldValue("ownerCountryCode", item.dial_code);
    setShowSelectCountryCodeModal(false);
  }

  function generateOwnerId() {
    generateUniqueId(formik.values.ownerName, searchUser, (id: string) =>
      formik.setFieldValue("ownerFarmUserId", id)
    );
  }

  async function selectExistingDealer(user: any, token: number) {
    // The lookup only carries name/mobile/countryCode (a lightweight
    // query) -- fetch the full record so the shop-level fields below
    // (WhatsApp/email/GST/addresses) can be prefilled too, not just the
    // owner identity fields, rather than making the salesperson retype
    // info this dealer's account may already have. The rest of the form is
    // still locked at this point (nothing to clobber), so every matched
    // field can just be written directly.
    const full = await getUser(user.farmUserId).catch(() => user);
    // Re-check staleness after this second await -- a newer search could
    // have superseded this one while getUser() was in flight.
    if (token !== searchTokenRef.current) return;
    formik.setValues({
      ...formik.values,
      existingOwnerSelected: true,
      ownerFarmUserId: full.farmUserId ?? user.farmUserId ?? "",
      ownerName: full.name ?? user.name ?? "",
      ownerMobile: full.mobile ?? user.mobile ?? "",
      ownerCountryCode: full.countryCode ?? user.countryCode ?? "+91",
      ownerEmail: full.email ?? "",
      whatsappNo: full.mobile ?? user.mobile ?? "",
      email: full.email ?? "",
      gstNo: full.gst ?? "",
      billingAddress: full.billingAddress ?? "",
      shippingAddress: full.shippingAddress ?? "",
    });
  }

  function useDifferentDealer() {
    formik.setValues({
      ...formik.values,
      existingOwnerSelected: false,
      ownerFarmUserId: "",
      ownerName: "",
      ownerMobile: "",
      ownerEmail: "",
      // Also clear the shop-level fields the previous match prefilled --
      // otherwise they'd silently carry over into whatever gets picked (or
      // typed as new) next, attributing one dealer's WhatsApp/email/GST/
      // addresses to a different dealer's location.
      whatsappNo: "",
      email: "",
      gstNo: "",
      billingAddress: "",
      shippingAddress: "",
    });
    lastAutoFilledRef.current = { whatsappNo: "", email: "" };
    lastSearchedMobileRef.current = "";
    setSearchAttempted(false);
    setSearchError(false);
  }

  // Ground salespeople end up typing near-identical contact info for the
  // dealer and the shop (WhatsApp/email are usually the dealer's own
  // mobile/email) -- these pre-fill the corresponding InventoryLocation
  // field as each owner field is typed, without clobbering it if the
  // salesperson has edited the shop-level field themselves to something
  // genuinely different. Deliberately excludes businessName -- the shop
  // name is never assumed to match the dealer's name, so it's always
  // typed by hand.
  //
  // Can't just check "is the shop field currently blank" per keystroke --
  // after the first character fills it (e.g. whatsappNo becomes "9"), it's
  // no longer blank, so that check would stop the cascade dead after one
  // character. Instead track the last value *we* wrote there, and keep
  // cascading as long as the field still equals that (i.e. the user hasn't
  // typed into it directly since).
  const lastAutoFilledRef = useRef<{ whatsappNo: string; email: string }>({
    whatsappNo: "",
    email: "",
  });

  function cascadeIfUnedited(field: "whatsappNo" | "email", value: string) {
    const current = formik.values[field];
    if (!current || current === lastAutoFilledRef.current[field]) {
      formik.setFieldValue(field, value);
      lastAutoFilledRef.current[field] = value;
    }
  }

  function onOwnerMobileChange(e: any) {
    formik.handleChange(e);
    cascadeIfUnedited("whatsappNo", e?.detail?.value ?? "");
  }

  function onOwnerEmailChange(e: any) {
    formik.handleChange(e);
    cascadeIfUnedited("email", e?.detail?.value ?? "");
  }

  if (formik.values.existingOwnerSelected) {
    return (
      <IonItem lines="none">
        <IonLabel>
          <strong>{formik.values.ownerName}</strong>
          <p>
            {formik.values.ownerCountryCode} {formik.values.ownerMobile}
          </p>
        </IonLabel>
        <IonButton slot="end" size="small" fill="clear" onClick={useDifferentDealer}>
          Change
        </IonButton>
      </IonItem>
    );
  }

  const fieldsLocked = !resolved;

  return (
    <>
      <IonItem>
        <IonLabel position="stacked">Country Code</IonLabel>
        <IonInput
          id="ownerCountryCode"
          name="ownerCountryCode"
          readonly
          value={formik.values.ownerCountryCode}
          placeholder="Country Code"
          onClick={() => setShowSelectCountryCodeModal(true)}
        ></IonInput>
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">Dealer Mobile Number</IonLabel>
        <IonInput
          id="ownerMobile"
          name="ownerMobile"
          type="tel"
          inputmode="tel"
          maxlength={12}
          value={formik.values.ownerMobile}
          placeholder="10-digit mobile number"
          onIonChange={onOwnerMobileChange}
        ></IonInput>
        {searching && <IonSpinner name="crescent" slot="end" />}
      </IonItem>
      {formik.touched.ownerMobile && formik.errors.ownerMobile ? (
        <div className="errorMsg">{formik.errors.ownerMobile}</div>
      ) : null}

      {!searching && searchAttempted && (
        <IonItem lines="none">
          <IonLabel class="secondary-text">No existing dealer found with this number. Fill details below to create a new one.</IonLabel>
        </IonItem>
      )}

      {!searching && searchError && (
        <IonItem lines="none">
          <IonLabel color="danger" class="ion-text-wrap">
            Couldn't check for an existing dealer -- check your connection and try again.
          </IonLabel>
          <IonButton slot="end" size="small" fill="clear" onClick={() => runSearch(formik.values.ownerMobile)}>
            Retry
          </IonButton>
        </IonItem>
      )}

      <IonItem>
        <IonLabel position="stacked">Dealer Name</IonLabel>
        <IonInput
          id="ownerName"
          name="ownerName"
          disabled={fieldsLocked}
          onBlur={() => generateOwnerId()}
          value={formik.values.ownerName}
          placeholder="Owner's Name"
          onIonChange={formik.handleChange}
        ></IonInput>
      </IonItem>
      {formik.touched.ownerName && formik.errors.ownerName ? (
        <div className="errorMsg">{formik.errors.ownerName}</div>
      ) : null}

      <IonItem>
        <IonLabel position="stacked">Dealer Email Id</IonLabel>
        <IonInput
          id="ownerEmail"
          name="ownerEmail"
          type="email"
          inputmode="email"
          disabled={fieldsLocked}
          value={formik.values.ownerEmail}
          placeholder="Email Id"
          onIonChange={onOwnerEmailChange}
        ></IonInput>
      </IonItem>
      {formik.touched.ownerEmail && formik.errors.ownerEmail ? (
        <div className="errorMsg">{formik.errors.ownerEmail}</div>
      ) : null}

      <IonItem>
        <IonLabel position="stacked">Password</IonLabel>
        <IonInput
          id="ownerPassword"
          name="ownerPassword"
          type="password"
          disabled={fieldsLocked}
          value={formik.values.ownerPassword}
          placeholder="Password (optional)"
          onIonChange={formik.handleChange}
        ></IonInput>
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">Gender</IonLabel>
        <IonSelect
          id="ownerGender"
          name="ownerGender"
          disabled={fieldsLocked}
          value={formik.values.ownerGender}
          placeholder="Select Gender"
          onIonChange={formik.handleChange}
        >
          <IonSelectOption value="Female">Female</IonSelectOption>
          <IonSelectOption value="Male">Male</IonSelectOption>
          <IonSelectOption value="Other">Other</IonSelectOption>
        </IonSelect>
      </IonItem>

      {showSelectCountryCodeModal && (
        <IonModal isOpen={showSelectCountryCodeModal}>
          <SelectItem
            label="Select Country"
            items={countryCodes}
            labelKey="name"
            valueKey="dial_code"
            setItem={setCountryCode}
            selectedValue=""
            onClose={setShowSelectCountryCodeModal}
          />
        </IonModal>
      )}
    </>
  );
};

export default DealerOwnerFields;
